import * as RadixPopover from '@radix-ui/react-popover'
import * as RadixSelect from '@radix-ui/react-select'
import { Tag, type TagSize } from '@trustoo/components/Tag/v2'
import { clsx } from 'clsx'
import {
    forwardRef,
    type KeyboardEvent,
    type ReactNode,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react'

export type SelectSize = 'sm' | 'md' | 'lg'
export type SelectType =
    | 'default'
    | 'icon-leading'
    | 'avatar-leading'
    | 'dot-leading'
    | 'search'
    | 'tags'

export interface SelectOption {
    value: string
    label: ReactNode
    supportingText?: ReactNode
    disabled?: boolean
    iconLeading?: ReactNode
    avatarSrc?: string
    avatarInitials?: string
    dotClassName?: string
}

export interface SelectProps {
    options: SelectOption[]
    size?: SelectSize
    type?: SelectType
    value?: string
    defaultValue?: string
    onChange?: (value: string) => void
    multiple?: boolean
    values?: string[]
    defaultValues?: string[]
    onChangeMultiple?: (values: string[]) => void

    placeholder?: ReactNode
    label?: ReactNode
    hintText?: ReactNode
    helpIcon?: boolean
    required?: boolean
    disabled?: boolean
    destructive?: boolean
    error?: string | boolean
    leadingIcon?: ReactNode
    shortcut?: ReactNode
    emptyMessage?: ReactNode

    unstyled?: boolean

    id?: string
    name?: string
    className?: string
    contentClassName?: string
    'aria-label'?: string
}

interface SizeClasses {
    field: string
    text: string
    hint: string
    icon: string
    avatar: string
    valueGap: string
    menuWrapper: string
    menuRow: string
    menuTextGap: string
    tag: TagSize
    tagAvatar: string
}

const SIZE_CLASSES: Record<SelectSize, SizeClasses> = {
    sm: {
        field: 'gap-spacing-md py-spacing-md pl-spacing-lg pr-2.5',
        text: 'text-sm',
        hint: 'text-xs',
        icon: 'size-4',
        avatar: 'size-5',
        valueGap: 'gap-spacing-sm',
        menuWrapper: 'px-spacing-xs py-px',
        menuRow: 'gap-spacing-md p-spacing-md text-sm data-[state=checked]:pr-2.5',
        menuTextGap: 'gap-spacing-sm',
        tag: 'sm',
        tagAvatar: 'size-3.5',
    },
    md: {
        field: 'gap-spacing-md px-spacing-lg py-spacing-md',
        text: 'text-md',
        hint: 'text-sm',
        icon: 'size-4',
        avatar: 'size-5',
        valueGap: 'gap-spacing-sm',
        menuWrapper: 'px-spacing-sm py-px',
        menuRow: 'gap-spacing-md p-spacing-md text-md data-[state=checked]:pr-2.5',
        menuTextGap: 'gap-spacing-md',
        tag: 'md',
        tagAvatar: 'size-4',
    },
    lg: {
        field: 'gap-spacing-md px-3.5 py-2.5',
        text: 'text-md',
        hint: 'text-sm',
        icon: 'size-5',
        avatar: 'size-6',
        valueGap: 'gap-spacing-md',
        menuWrapper: 'px-spacing-sm py-px',
        menuRow: 'gap-spacing-md py-2.5 pl-spacing-md pr-2.5 text-md',
        menuTextGap: 'gap-spacing-md',
        tag: 'md',
        tagAvatar: 'size-4',
    },
}

const FIELD_BASE =
    'flex w-full items-center rounded-radius-md border border-solid bg-background-bg-primary font-body shadow-xs outline-none transition-colors data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50'

const FIELD_DEFAULT =
    'border-border-primary data-[state=open]:border-border-brand data-[state=open]:ring-1 data-[state=open]:ring-inset data-[state=open]:ring-focus-ring focus-visible:border-border-brand focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-focus-ring focus-within:border-border-brand focus-within:ring-1 focus-within:ring-inset focus-within:ring-focus-ring'

const FIELD_ERROR =
    'border-border-error data-[state=open]:ring-1 data-[state=open]:ring-inset data-[state=open]:ring-focus-ring-error focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-focus-ring-error focus-within:ring-1 focus-within:ring-inset focus-within:ring-focus-ring-error'

const FIELD_UNSTYLED =
    'flex items-center gap-spacing-xs bg-base-transparent font-body text-md font-medium text-text-primary outline-none data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50'

const MENU_SURFACE =
    'z-50 overflow-hidden rounded-radius-md bg-background-bg-primary font-body shadow-lg ring-1 ring-alpha-black-10'

const MENU_ROW_BASE =
    'flex w-full cursor-pointer items-center rounded-radius-sm border-0 bg-base-transparent text-left outline-none transition-colors hover:bg-background-bg-primary_hover focus-visible:bg-background-bg-primary_hover data-[highlighted]:bg-background-bg-primary_hover data-[state=checked]:bg-background-bg-brand-primary data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50'

const CHEVRON_DOWN_PATH = 'm6 9 6 6 6-6'
const SEARCH_PATH = 'm21 21-4.35-4.35M19 11a8 8 0 1 1-16 0 8 8 0 0 1 16 0Z'
const USER_PATH =
    'M20 21c0-1.396 0-2.093-.172-2.661a4 4 0 0 0-2.667-2.667C16.593 15.5 15.896 15.5 14.5 15.5h-5c-1.396 0-2.093 0-2.661.172a4 4 0 0 0-2.667 2.667C4 18.907 4 19.604 4 21M16.5 7.5a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0Z'
const HELP_PATH =
    'M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3m.08 4h.01M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z'
const CHECK_PATH = 'M20 6 9 17l-5-5'

function StrokeIcon({ path, className }: { path: string; className?: string }) {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className={clsx('shrink-0', className)}>
            <path d={path} />
        </svg>
    )
}

function OptionAvatar({ option, className }: { option?: SelectOption; className: string }) {
    let content: ReactNode = <StrokeIcon path={USER_PATH} className="size-3/5" />
    if (option?.avatarSrc) {
        content = <img src={option.avatarSrc} alt="" className="size-full object-cover" />
    } else if (option?.avatarInitials) {
        content = option.avatarInitials
    }

    return (
        <span
            aria-hidden="true"
            className={clsx(
                'flex shrink-0 items-center justify-center overflow-hidden rounded-radius-full bg-background-bg-tertiary text-xs font-semibold text-text-tertiary ring-[0.5px] ring-inset ring-alpha-black-10',
                className,
            )}>
            {content}
        </span>
    )
}

function OptionDot({ option }: { option?: SelectOption }) {
    return (
        <span aria-hidden="true" className="flex size-2 shrink-0 items-center justify-center">
            <span
                className={clsx(
                    'size-1.5 rounded-radius-full',
                    option?.dotClassName ?? 'bg-foreground-fg-success-secondary',
                )}
            />
        </span>
    )
}

function optionText(option: SelectOption): string {
    const label = typeof option.label === 'string' ? option.label : ''
    const supporting = typeof option.supportingText === 'string' ? option.supportingText : ''

    return `${label} ${supporting}`.toLowerCase()
}

export const Select = forwardRef<HTMLButtonElement, SelectProps>(
    (
        {
            options,
            size = 'md',
            type = 'default',
            value,
            defaultValue,
            onChange,
            multiple = false,
            values,
            defaultValues = [],
            onChangeMultiple,
            placeholder = 'Select...',
            label,
            hintText,
            helpIcon,
            required,
            disabled,
            destructive,
            error,
            leadingIcon,
            shortcut,
            emptyMessage = 'No options found',
            unstyled = false,
            id,
            name,
            className,
            contentClassName,
            'aria-label': ariaLabel,
        },
        ref,
    ) => {
        const generatedId = useId()
        const triggerId = id ?? `${generatedId}-trigger`
        const labelId = `${generatedId}-label`
        const hintId = `${generatedId}-hint`
        const listboxId = `${generatedId}-listbox`

        const anchorRef = useRef<HTMLDivElement>(null)
        const inputRef = useRef<HTMLInputElement>(null)

        const [internalValue, setInternalValue] = useState(defaultValue ?? '')
        const [internalValues, setInternalValues] = useState<string[]>(defaultValues)
        const [searchQuery, setSearchQuery] = useState<string | null>(null)
        const [isOpen, setIsOpen] = useState(false)

        const s = SIZE_CLASSES[size]
        const isMultiple = type === 'tags' || multiple
        const hasInput = type === 'search' || type === 'tags'
        const usesPopover = hasInput || isMultiple
        const isError = Boolean(destructive) || Boolean(error)

        const currentValue = value === undefined ? internalValue : value
        const currentValues = values === undefined ? internalValues : values

        const selectedOption = options.find((option) => option.value === currentValue)

        const filteredOptions = useMemo(() => {
            const query = (searchQuery ?? '').trim().toLowerCase()
            if (!hasInput || query === '') return options

            return options.filter((option) => optionText(option).includes(query))
        }, [options, searchQuery, hasInput])

        let message: ReactNode = hintText
        if (typeof error === 'string') {
            message = error
        } else if (error === true && !hintText) {
            message = 'This field is invalid'
        }

        const handleSingleSelect = (nextValue: string) => {
            if (value === undefined) setInternalValue(nextValue)
            onChange?.(nextValue)
        }

        const handleMultipleSelect = (nextValues: string[]) => {
            if (values === undefined) setInternalValues(nextValues)
            onChangeMultiple?.(nextValues)
        }

        const toggleValue = (optionValue: string) => {
            if (isMultiple) {
                const nextValues = currentValues.includes(optionValue)
                    ? currentValues.filter((v) => v !== optionValue)
                    : [...currentValues, optionValue]
                handleMultipleSelect(nextValues)
                setSearchQuery(null)
                inputRef.current?.focus()
            } else {
                handleSingleSelect(optionValue)
                setSearchQuery(null)
                setIsOpen(false)
            }
        }

        const removeValue = (valueToRemove: string) => {
            handleMultipleSelect(currentValues.filter((v) => v !== valueToRemove))
        }

        const handleOpenChange = (open: boolean) => {
            setIsOpen(open)
            if (!open) setSearchQuery(null)
        }

        const fieldClasses = clsx(
            FIELD_BASE,
            s.field,
            s.text,
            isError ? FIELD_ERROR : FIELD_DEFAULT,
            className,
        )

        const renderTriggerLeading = (): ReactNode => {
            if (leadingIcon) {
                return (
                    <span className={clsx('flex shrink-0 text-foreground-fg-quaternary', s.icon)}>
                        {leadingIcon}
                    </span>
                )
            }
            if (type === 'icon-leading') {
                return (
                    <span className={clsx('flex shrink-0 text-foreground-fg-quaternary', s.icon)}>
                        {selectedOption?.iconLeading ?? (
                            <StrokeIcon path={USER_PATH} className="size-full" />
                        )}
                    </span>
                )
            }
            if (type === 'avatar-leading') {
                return <OptionAvatar option={selectedOption} className={s.avatar} />
            }
            if (type === 'dot-leading' && selectedOption) {
                return <OptionDot option={selectedOption} />
            }

            return null
        }

        const renderRowLeading = (option: SelectOption): ReactNode => {
            if (type === 'icon-leading') {
                return (
                    <span className={clsx('flex shrink-0 text-foreground-fg-quaternary', s.icon)}>
                        {option.iconLeading ?? (
                            <StrokeIcon path={USER_PATH} className="size-full" />
                        )}
                    </span>
                )
            }
            if (type === 'avatar-leading') {
                return <OptionAvatar option={option} className={s.avatar} />
            }
            if (type === 'dot-leading') {
                return <OptionDot option={option} />
            }
            if (option.avatarSrc || option.avatarInitials) {
                return <OptionAvatar option={option} className={s.avatar} />
            }
            if (option.iconLeading) {
                return (
                    <span className={clsx('flex shrink-0 text-foreground-fg-quaternary', s.icon)}>
                        {option.iconLeading}
                    </span>
                )
            }

            return null
        }

        const renderRowText = (option: SelectOption, labelContent: ReactNode) => (
            <span className={clsx('flex min-w-0 flex-1 items-center', s.menuTextGap)}>
                {labelContent}
                {option.supportingText && (
                    <span className="truncate text-text-tertiary">{option.supportingText}</span>
                )}
            </span>
        )

        const checkIcon = (
            <StrokeIcon
                path={CHECK_PATH}
                className={clsx('ml-auto text-foreground-fg-brand-primary', s.icon)}
            />
        )

        const chevron = (
            <StrokeIcon
                path={CHEVRON_DOWN_PATH}
                className={clsx(
                    'text-foreground-fg-quaternary transition-transform',
                    s.icon,
                    isOpen && 'rotate-180',
                )}
            />
        )

        const shortcutNode = shortcut ? (
            <span className="shrink-0 rounded-radius-xs border border-solid border-border-secondary px-spacing-xs py-px font-body text-xs font-medium text-text-quaternary">
                {shortcut}
            </span>
        ) : null

        const labelNode = label ? (
            <div className="flex items-center gap-spacing-xxs">
                <label
                    id={labelId}
                    htmlFor={triggerId}
                    className="font-body text-sm font-medium text-text-secondary">
                    {label}
                </label>
                {required && (
                    <span
                        aria-hidden="true"
                        className="font-body text-sm font-medium text-text-brand-tertiary">
                        *
                    </span>
                )}
                {helpIcon && (
                    <span title="Help information" className="flex cursor-help">
                        <StrokeIcon
                            path={HELP_PATH}
                            className="size-4 text-foreground-fg-quaternary"
                        />
                    </span>
                )}
            </div>
        ) : null

        const hintNode = message ? (
            <p
                id={hintId}
                className={clsx(
                    'm-spacing-none font-body',
                    s.hint,
                    isError ? 'text-text-error' : 'text-text-tertiary',
                )}>
                {message}
            </p>
        ) : null

        const hiddenInput = name ? (
            <input
                type="hidden"
                name={name}
                value={isMultiple ? currentValues.join(',') : currentValue}
            />
        ) : null

        const describedBy = message ? hintId : undefined
        const labelledBy = label ? labelId : undefined

        if (usesPopover) {
            const selectedLabelText =
                selectedOption && typeof selectedOption.label === 'string'
                    ? selectedOption.label
                    : ''
            const idleInputValue = isMultiple ? '' : selectedLabelText
            const inputValue = searchQuery ?? idleInputValue
            const placeholderText = typeof placeholder === 'string' ? placeholder : undefined

            const handleFieldClick = () => {
                if (disabled) return
                if (hasInput) {
                    inputRef.current?.focus()
                    setIsOpen(true)
                    return
                }
                setIsOpen((open) => !open)
            }

            const handleFieldKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
                if (disabled || hasInput) return
                if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
                    event.preventDefault()
                    setIsOpen(true)
                }
            }

            const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
                if (event.key === 'ArrowDown') {
                    event.preventDefault()
                    setIsOpen(true)
                }
                if (
                    event.key === 'Backspace' &&
                    type === 'tags' &&
                    inputValue === '' &&
                    currentValues.length > 0
                ) {
                    removeValue(currentValues[currentValues.length - 1])
                }
            }

            const searchInput = (
                <input
                    ref={inputRef}
                    id={triggerId}
                    type="text"
                    role="combobox"
                    aria-expanded={isOpen}
                    aria-controls={listboxId}
                    aria-autocomplete="list"
                    aria-label={ariaLabel}
                    aria-labelledby={labelledBy}
                    aria-describedby={describedBy}
                    aria-invalid={isError || undefined}
                    autoComplete="off"
                    value={inputValue}
                    placeholder={
                        type === 'tags' && currentValues.length > 0 ? undefined : placeholderText
                    }
                    disabled={disabled}
                    onChange={(event) => {
                        setSearchQuery(event.target.value)
                        setIsOpen(true)
                    }}
                    onKeyDown={handleInputKeyDown}
                    className={clsx(
                        'min-w-0 flex-1 border-0 bg-base-transparent p-spacing-none font-body font-medium text-text-primary outline-none placeholder:font-regular placeholder:text-text-placeholder disabled:cursor-not-allowed',
                        s.text,
                    )}
                />
            )

            let fieldContent: ReactNode
            if (type === 'search') {
                fieldContent = (
                    <>
                        <StrokeIcon
                            path={SEARCH_PATH}
                            className={clsx('text-foreground-fg-quaternary', s.icon)}
                        />
                        {searchInput}
                        {shortcutNode}
                    </>
                )
            } else if (type === 'tags') {
                fieldContent = (
                    <>
                        <StrokeIcon
                            path={SEARCH_PATH}
                            className={clsx('text-foreground-fg-quaternary', s.icon)}
                        />
                        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-spacing-sm">
                            {currentValues.map((selected) => {
                                const option = options.find((o) => o.value === selected)
                                if (!option) return null
                                const hasAvatar = Boolean(option.avatarSrc || option.avatarInitials)

                                return (
                                    <span
                                        key={selected}
                                        className="flex"
                                        onPointerDown={(event) => event.stopPropagation()}
                                        onClick={(event) => event.stopPropagation()}>
                                        <Tag
                                            size={s.tag}
                                            avatar={
                                                hasAvatar ? (
                                                    <OptionAvatar
                                                        option={option}
                                                        className={s.tagAvatar}
                                                    />
                                                ) : undefined
                                            }
                                            onClose={() => removeValue(selected)}
                                            disabled={disabled}>
                                            {option.label}
                                        </Tag>
                                    </span>
                                )
                            })}
                            {searchInput}
                        </div>
                        {shortcutNode}
                    </>
                )
            } else {
                const multipleLabel =
                    currentValues.length > 0 ? `${currentValues.length} selected` : null
                fieldContent = (
                    <>
                        {renderTriggerLeading()}
                        {multipleLabel ? (
                            <span className="min-w-0 flex-1 truncate text-left font-medium text-text-primary">
                                {multipleLabel}
                            </span>
                        ) : (
                            <span className="min-w-0 flex-1 truncate text-left text-text-placeholder">
                                {placeholder}
                            </span>
                        )}
                        {chevron}
                    </>
                )
            }

            return (
                <div className="flex w-full flex-col gap-spacing-sm">
                    {labelNode}

                    <RadixPopover.Root open={isOpen} onOpenChange={handleOpenChange}>
                        <RadixPopover.Anchor asChild>
                            <div
                                ref={anchorRef}
                                id={hasInput ? undefined : triggerId}
                                role={hasInput ? undefined : 'combobox'}
                                aria-expanded={hasInput ? undefined : isOpen}
                                aria-controls={hasInput ? undefined : listboxId}
                                aria-label={hasInput ? undefined : ariaLabel}
                                aria-labelledby={hasInput ? undefined : labelledBy}
                                aria-describedby={hasInput ? undefined : describedBy}
                                aria-disabled={disabled || undefined}
                                tabIndex={hasInput || disabled ? undefined : 0}
                                data-state={isOpen ? 'open' : 'closed'}
                                data-disabled={disabled ? '' : undefined}
                                onClick={handleFieldClick}
                                onKeyDown={handleFieldKeyDown}
                                className={clsx(fieldClasses, 'cursor-pointer')}>
                                {fieldContent}
                            </div>
                        </RadixPopover.Anchor>

                        <RadixPopover.Portal>
                            <RadixPopover.Content
                                id={listboxId}
                                role="listbox"
                                aria-multiselectable={isMultiple || undefined}
                                align="start"
                                sideOffset={4}
                                onOpenAutoFocus={(event) => event.preventDefault()}
                                onInteractOutside={(event) => {
                                    const target = event.target as Node | null
                                    if (target && anchorRef.current?.contains(target)) {
                                        event.preventDefault()
                                    }
                                }}
                                className={clsx(
                                    MENU_SURFACE,
                                    'max-h-56 w-[var(--radix-popover-trigger-width)] overflow-y-auto py-spacing-xs',
                                    contentClassName,
                                )}>
                                {filteredOptions.length === 0 && (
                                    <div className={s.menuWrapper}>
                                        <div className={clsx('flex text-text-tertiary', s.menuRow)}>
                                            {emptyMessage}
                                        </div>
                                    </div>
                                )}
                                {filteredOptions.map((option) => {
                                    const isSelected = isMultiple
                                        ? currentValues.includes(option.value)
                                        : currentValue === option.value

                                    return (
                                        <div
                                            key={option.value}
                                            role="presentation"
                                            className={s.menuWrapper}>
                                            <button
                                                type="button"
                                                role="option"
                                                aria-selected={isSelected}
                                                disabled={option.disabled}
                                                data-state={isSelected ? 'checked' : 'unchecked'}
                                                data-disabled={option.disabled ? '' : undefined}
                                                onClick={() => toggleValue(option.value)}
                                                className={clsx(MENU_ROW_BASE, s.menuRow)}>
                                                {renderRowLeading(option)}
                                                {renderRowText(
                                                    option,
                                                    <span className="truncate font-medium text-text-primary">
                                                        {option.label}
                                                    </span>,
                                                )}
                                                {isSelected && checkIcon}
                                            </button>
                                        </div>
                                    )
                                })}
                            </RadixPopover.Content>
                        </RadixPopover.Portal>
                    </RadixPopover.Root>

                    {hintNode}
                    {hiddenInput}
                </div>
            )
        }

        const triggerClasses = unstyled
            ? clsx(FIELD_UNSTYLED, className)
            : clsx(fieldClasses, 'cursor-pointer')

        const root = (
            <RadixSelect.Root
                value={currentValue}
                onValueChange={handleSingleSelect}
                disabled={disabled}
                required={required}
                onOpenChange={setIsOpen}>
                <RadixSelect.Trigger
                    ref={ref}
                    id={triggerId}
                    aria-label={ariaLabel}
                    aria-labelledby={unstyled ? undefined : labelledBy}
                    aria-describedby={unstyled ? undefined : describedBy}
                    aria-invalid={isError || undefined}
                    className={triggerClasses}>
                    {renderTriggerLeading()}
                    {selectedOption ? (
                        <span
                            className={clsx(
                                'flex min-w-0 flex-1 items-center text-left',
                                s.valueGap,
                            )}>
                            <span className="truncate font-medium text-text-primary">
                                {selectedOption.label}
                            </span>
                            {selectedOption.supportingText && !unstyled && (
                                <span className="truncate text-text-tertiary">
                                    {selectedOption.supportingText}
                                </span>
                            )}
                        </span>
                    ) : (
                        <span className="min-w-0 flex-1 truncate text-left text-text-placeholder">
                            {placeholder}
                        </span>
                    )}
                    {chevron}
                </RadixSelect.Trigger>

                <RadixSelect.Portal>
                    <RadixSelect.Content
                        position="popper"
                        sideOffset={4}
                        className={clsx(
                            MENU_SURFACE,
                            'max-h-56',
                            unstyled ? 'min-w-44' : 'w-[var(--radix-select-trigger-width)]',
                            contentClassName,
                        )}>
                        <RadixSelect.Viewport className="py-spacing-xs">
                            {options.map((option) => (
                                <div key={option.value} className={s.menuWrapper}>
                                    <RadixSelect.Item
                                        value={option.value}
                                        disabled={option.disabled}
                                        className={clsx(MENU_ROW_BASE, s.menuRow)}>
                                        {renderRowLeading(option)}
                                        {renderRowText(
                                            option,
                                            <RadixSelect.ItemText asChild>
                                                <span className="truncate font-medium text-text-primary">
                                                    {option.label}
                                                </span>
                                            </RadixSelect.ItemText>,
                                        )}
                                        <RadixSelect.ItemIndicator className="ml-auto flex">
                                            {checkIcon}
                                        </RadixSelect.ItemIndicator>
                                    </RadixSelect.Item>
                                </div>
                            ))}
                        </RadixSelect.Viewport>
                    </RadixSelect.Content>
                </RadixSelect.Portal>
            </RadixSelect.Root>
        )

        if (unstyled) {
            return (
                <div className="inline-flex">
                    {root}
                    {hiddenInput}
                </div>
            )
        }

        return (
            <div className="flex w-full flex-col gap-spacing-sm">
                {labelNode}
                {root}
                {hintNode}
                {hiddenInput}
            </div>
        )
    },
)

Select.displayName = 'Select'
